import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  Download,
  Target,
  CheckCircle,
  XCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetVendorMasterService from '../../services/assetVendorMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const AssetVendorMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalVendors: 0,
    activeVendors: 0,
    inactiveVendors: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listResponse, statsResponse] = await Promise.all([
        assetVendorMasterService.getAllVendors({ limit: 1000 }),
        assetVendorMasterService.getVendorStats()
      ])

      if (listResponse.success) {
        const transformedData = (listResponse.data || []).map(item => ({
          ...item,
          id: item._id
        }))
        setData(transformedData)
        if (listResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No asset vendor records found. Create your first vendor!')
        }
      } else {
        toast.error(listResponse.message || 'Failed to load asset vendors')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading asset vendor records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view asset vendors')
      } else if (!error._errorHandled) {
        toast.error('Failed to load asset vendor records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'vendorCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'vendorName',
      header: 'Vendor Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'contactName',
      header: 'Contact',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'services',
      header: 'Services',
      cell: ({ getValue }) => {
        const services = getValue() || []
        return (
          <div style={{ textAlign: 'left' }}>{services.length > 0 ? services.join(', ') : '-'}</div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/assets/vendor-master/form')
  }

  const handleEdit = (vendorData) => {
    navigate('/assets/vendor-master/edit', { state: { vendorData } })
  }

  const handleView = (vendorData) => {
    navigate('/assets/vendor-master/view', { state: { vendorData } })
  }

  const handleDelete = (vendorData) => {
    setDeleteItem(vendorData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting asset vendor...')
      const response = await assetVendorMasterService.deleteVendor(deleteItem._id)
      if (response.success) {
        toast.success('Asset vendor deleted successfully!', { autoClose: 2000 })
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete asset vendor')
      }
    } catch (error) {
      console.error('Error deleting asset vendor:', error)
      if (error.response?.status === 404) {
        toast.error('Asset vendor not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this asset vendor')
      } else if (!error._errorHandled) {
        toast.error('Failed to delete asset vendor. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Vendors', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Asset Vendors</h1>
          <p>Asset suppliers and service providers</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Vendor
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalVendors || 0}</div>
          <div className="metric-title">Total Vendors</div>
          <div className="metric-subtitle">All vendors</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeVendors || 0}</div>
          <div className="metric-title">Active Vendors</div>
          <div className="metric-subtitle">Currently in use</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveVendors || 0}</div>
          <div className="metric-title">Inactive Vendors</div>
          <div className="metric-subtitle">Not in use</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          searchable={true}
          searchPlaceholder="Search vendors..."
          noCard={true}
          pageSize={10}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Asset Vendor"
        message={`Are you sure you want to delete "${deleteItem?.vendorName || deleteItem?.vendorCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default AssetVendorMaster
